import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEOHelmet } from "@/components/SEOHelmet";
import { FreshnessNote } from "@/components/FreshnessNote";
import { PopularCardsSection } from "@/components/PopularCardsSection";
import { Link, useNavigate } from "@/lib/router-compat";
import { useCallback } from "react";
import { getSiteOrigin } from "@/lib/ssr-safe";
import { reviewDateISO } from "@/lib/currentYear";
import { useCards } from "@/hooks/useCards";

export default function Methodik() {
  const navigate = useNavigate();
  const handleNavigate = useCallback(
    (page: string) => {
      if (page === "home") navigate("/");
    },
    [navigate],
  );
  const { data: cards = [] } = useCards();
  const cardCount = cards.length;
  const url = `${getSiteOrigin()}/methodik`;

  const title = "Methodik: So vergleichen wir Kreditkarten";
  const description =
    "Woher unsere Kartendaten stammen, wie wir Karten einordnen und sortieren, wie oft wir prüfen und wie wir Geld verdienen – transparent erklärt.";

  return (
    <div className="min-h-screen bg-background">
      <SEOHelmet
        title={`${title} | Kreditkarten.de`}
        description={description}
        url={url}
        type="website"
        modifiedTime={reviewDateISO()}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: title,
          description,
          url,
          dateModified: reviewDateISO(),
          publisher: {
            "@type": "Organization",
            name: "Kreditkarten.de",
            url: "https://www.kreditkarten.de",
          },
        }}
      />
      <Header onNavigate={handleNavigate} currentPage="methodik" />

      <main className="container mx-auto px-4 py-10 max-w-3xl">
        <nav className="text-sm text-muted-foreground mb-4">
          <Link to="/" className="hover:text-primary">
            Startseite
          </Link>{" "}
          <span aria-hidden="true">›</span> Methodik
        </nav>

        <h1 className="text-3xl md:text-4xl font-bold mb-3">
          So vergleichen wir Kreditkarten
        </h1>
        <p className="text-lg text-muted-foreground mb-2">
          Wir wollen nachvollziehbar sein. Auf dieser Seite steht, woher unsere Daten kommen,
          wie wir Karten einordnen, wonach wir sortieren und womit wir Geld verdienen.
        </p>
        <FreshnessNote className="mb-10" label="Zuletzt geprüft" showMethodLink={false} />

        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-3">Welche Karten wir zeigen</h2>
          <p className="text-muted-foreground mb-3">
            Unser Vergleich umfasst derzeit {cardCount > 0 ? `${cardCount} ` : ""}Karten von
            Banken und Kartenanbietern, die sich in Deutschland beantragen lassen. Wir zeigen
            keine Angebote, die nur regional, nur für Bestandskunden oder nur über eine Filiale
            erhältlich sind, wenn wir die Konditionen nicht öffentlich nachprüfen können.
          </p>
          <p className="text-muted-foreground">
            Wir nehmen bewusst auch Karten auf, mit denen wir nichts verdienen, wenn sie für
            eine Frage die passende Antwort sind – etwa günstige Karten von Direktbanken.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-3">Woher die Daten kommen</h2>
          <ul className="space-y-3 text-muted-foreground">
            <li>
              <strong className="text-foreground">Anbieterdaten:</strong> Konditionen wie
              Jahresgebühr, Sollzins, effektiver Jahreszins, Fremdwährungsentgelt und
              Bargeldgebühren übernehmen wir aus den offiziellen Angaben der Kartenherausgeber
              und deren Partnerprogrammen.
            </li>
            <li>
              <strong className="text-foreground">Preis- und Leistungsverzeichnisse:</strong> Bei
              Unklarheiten gilt das, was der Anbieter in seinen verbindlichen Unterlagen
              veröffentlicht.
            </li>
            <li>
              <strong className="text-foreground">Keine Schätzungen:</strong> Felder, für die uns
              keine belastbare Angabe vorliegt, lassen wir leer statt sie zu raten. Deshalb steht
              auf manchen Karten „k. A.“.
            </li>
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-3">Wie wir Karten einordnen</h2>
          <p className="text-muted-foreground mb-3">
            „Kreditkarte“ ist in Deutschland kein geschützter Begriff. Wir unterscheiden deshalb
            vier Typen und zeigen bei jeder Karte an, um welchen es sich handelt:
          </p>
          <ul className="space-y-2 text-muted-foreground">
            <li>
              <strong className="text-foreground">Revolving-Kreditkarte</strong> – echter
              Kreditrahmen mit Teilzahlungsoption. Nur bei diesem Typ nennen wir Sollzins,
              effektiven Jahreszins und ein Rückzahlungsbeispiel.
            </li>
            <li>
              <strong className="text-foreground">Charge-Karte</strong> – monatliche
              Komplettabbuchung, keine Teilzahlung, keine Sollzinsen.
            </li>
            <li>
              <strong className="text-foreground">Debitkarte</strong> – sofortige Abbuchung vom
              Girokonto. Zinsen entstehen nur über den Dispo des Kontos, nicht über die Karte.
            </li>
            <li>
              <strong className="text-foreground">Prepaid-Karte</strong> – nur mit Guthaben
              nutzbar, keine Bonitätsprüfung, keine Kreditkennzahlen.
            </li>
          </ul>
          <p className="text-muted-foreground mt-3">
            Kennzahlen, die für einen Kartentyp keinen Sinn ergeben, blenden wir aus, statt eine
            Null hinzuschreiben. Eine Prepaid-Karte bekommt bei uns keinen „0 % Effektivzins“.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-3">Wonach wir sortieren</h2>
          <p className="text-muted-foreground mb-3">
            Es gibt bei uns keine Sternebewertung und keinen „Testsieger“. Wir bewerten Karten
            nicht redaktionell mit Punkten, weil die passende Karte davon abhängt, wofür Sie sie
            benutzen.
          </p>
          <p className="text-muted-foreground mb-3">
            Stattdessen sortieren wir nach dem, was Sie auswählen: Jahresgebühr, Kosten im
            Ausland, Bargeldgebühren, Kartentyp oder Versicherungen. Die Reihenfolge ändert sich
            mit Ihrer Auswahl – nicht mit unserer Meinung.
          </p>
          <p className="text-muted-foreground">
            Auszeichnungen auf den Karten sind reine Fakten aus den Daten, zum Beispiel „0 €
            Jahresgebühr“ oder „Keine Fremdwährungsgebühr“. Wir vergeben keine Labels wie
            „Empfehlung der Redaktion“.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-3">Wie oft wir prüfen</h2>
          <p className="text-muted-foreground">
            Die Kartendaten werden laufend aus den Anbieterquellen aktualisiert. Zusätzlich
            prüfen wir jeden Monat die Vergleichs- und Ratgeberseiten auf Aktualität. Das Datum
            unter „Stand“ auf jeder Seite zeigt diese letzte Prüfung.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-3">Womit wir Geld verdienen</h2>
          <p className="text-muted-foreground mb-3">
            Kreditkarten.de ist für Sie kostenlos. Wenn Sie über einen Link bei uns eine Karte
            beantragen, erhalten wir vom Anbieter in vielen Fällen eine Provision. Für Sie ändert
            sich dadurch nichts am Preis oder an den Konditionen.
          </p>
          <p className="text-muted-foreground mb-3">
            Einige Anbieter buchen zusätzlich eine feste Platzierung. Diese Platzierungen sind
            immer sichtbar mit <strong className="text-foreground">„Anzeige“</strong>
            gekennzeichnet. Wir verkaufen keine guten Bewertungen – wir vergeben ja keine.
          </p>
          <p className="text-muted-foreground">
            Karten ohne Provision werden nicht schlechter gestellt und nicht ausgeblendet.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-3">Was wir nicht leisten</h2>
          <p className="text-muted-foreground">
            Wir sind ein Vergleichsportal, keine Bank und keine Finanzberatung. Verbindlich sind
            immer die Konditionen des jeweiligen Anbieters zum Zeitpunkt Ihres Antrags. Ob ein
            Antrag bewilligt wird, entscheidet allein der Kartenherausgeber.
          </p>
        </section>

        <section className="mb-10 rounded-xl border bg-muted/30 p-6">
          <h2 className="text-xl font-bold mb-2">Fehler gefunden?</h2>
          <p className="text-muted-foreground">
            Wenn eine Angabe nicht stimmt, sagen Sie uns Bescheid – wir korrigieren sie. Wer
            hinter der Seite steht, lesen Sie unter{" "}
            <Link to="/ueber-uns" className="text-primary underline underline-offset-4">
              Über uns
            </Link>
            .
          </p>
        </section>
      </main>

      <PopularCardsSection
        currentPage="methodik"
        forceVisible
        onViewDetails={(slug) => navigate(`/kreditkarten/${slug}`)}
        onViewAllCards={() => navigate("/kreditkarten")}
      />
      <Footer onNavigate={handleNavigate} />
    </div>
  );
}
